import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Bot, Copy, Hand, Lock, MessageCircle, MessageSquare, RefreshCw, Trash2, User } from 'lucide-react';

import useAdminChatConversations from '../../hooks/useAdminChatConversations';
import useAdminChatConversation from '../../hooks/useAdminChatConversation';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { useConfirm } from '../../components/ui/ConfirmDialog';
import { parseApiError } from '../../utils/apiError';

// Phase 8 human handoff, restructured into a list + overview + a separate
// full-page editor: click a conversation for a quick read-only summary
// (started, message count, who it's with), click Interact to jump to
// AdminChatInteract.js (/admin/chat/:id) for the actual transcript/reply/
// take-over UI. Keeps the list page lightweight — no reply state, no
// per-message socket traffic, just the summary the overview needs. The row
// list itself does get one lightweight live feed (useAdminChatConversations'
// 'admin:chat' socket room) for mode changes and handoff requests, so a
// pending "Person requested" doesn't require sitting on Refresh.
const MODE_FILTERS = [
  { value: '', label: 'All' },
  { value: 'human', label: 'Person' },
  { value: 'bot', label: 'Bot' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Closed' },
];

const HANDOFF_FILTERS = [
  { value: false, label: 'All' },
  { value: true, label: 'Needs a Person' },
];

export default function AdminChatConsole() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToast } = useToast();
  const { confirm, dialog } = useConfirm();
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState('active');
  const [handoffOnly, setHandoffOnly] = useState(() => searchParams.get('handoff') === '1');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [selectedId, setSelectedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [overviewRefreshing, setOverviewRefreshing] = useState(false);

  const {
    items,
    total,
    pages,
    loading: listLoading,
    reload: reloadList,
    deleteConversation,
  } = useAdminChatConversations({ mode, status, handoffRequested: handoffOnly, page, limit });

  const {
    conversation,
    messages,
    aiStats,
    loading: overviewLoading,
    deleted: overviewDeleted,
    reload: reloadOverview,
  } = useAdminChatConversation(selectedId);
  const selfDeletedIdRef = useRef(null);

  // The customer deleted the selected conversation, or another rep did,
  // while it was open here — clear the selection and let the list's own
  // live feed (emitConversationDeleted's adminActivity broadcast) drop the
  // row. Uses the id itself rather than a boolean so it can't misfire
  // regardless of whether this fires before or after onDeleteConversation's
  // own state updates below settle.
  useEffect(() => {
    if (!overviewDeleted) return;
    if (selfDeletedIdRef.current === selectedId) {
      selfDeletedIdRef.current = null;
      return;
    }
    addToast('This conversation was deleted', { variant: 'error' });
    setSelectedId(null);
  }, [overviewDeleted, selectedId, addToast]);

  // A narrowed-down filter (mode/status/handoff) that leaves exactly one
  // match is almost always someone jumping in to handle that one chat —
  // e.g. the dashboard's "needs a person" card — so open it automatically
  // instead of making them click the only row.
  const filtersApplied = mode !== '' || status !== '' || handoffOnly;
  useEffect(() => {
    if (!filtersApplied || listLoading) return;
    if (items.length !== 1) return;
    if (selectedId === items[0]._id) return;
    setSelectedId(items[0]._id);
  }, [filtersApplied, listLoading, items, selectedId]);

  const setFilter = (m) => {
    setMode(m);
    setPage(1);
  };

  const setStatusFilter = (s) => {
    setStatus(s);
    setPage(1);
  };

  const setHandoffFilter = (h) => {
    setHandoffOnly(h);
    setPage(1);
  };

  const onChangeLimit = (l) => {
    setLimit(l);
    setPage(1);
  };

  // manual refresh — still needed for anything the 'admin:chat' live feed
  // deliberately doesn't handle: a brand-new conversation, or an existing
  // row whose mode/status change would move it onto a different page under
  // the current filter/sort (see useAdminChatConversations' comment).
  // reload() itself doesn't toggle the hook's `loading` (that's reserved
  // for the mode/page-driven effect), so this tracks its own in-flight
  // state for the button/icon.
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await reloadList();
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not refresh the list', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  // the overview's socket listener (useAdminChatConversation) only appends
  // new messages live — it never re-fetches aiStats, so the Advanced panel's
  // token/tool-iteration counts go stale the moment the customer/bot
  // exchange another message while this conversation is selected. This is
  // the only way to pull a fresh total short of deselecting and reselecting
  // the row.
  const onRefreshOverview = async () => {
    setOverviewRefreshing(true);
    try {
      await reloadOverview();
    } catch (err) {
      addToast(parseApiError(err).formError || 'Could not refresh the overview', { variant: 'error' });
    } finally {
      setOverviewRefreshing(false);
    }
  };

  // shared by the per-row delete button in the list and (were it added
  // there) the overview — deleteConversation takes the id directly rather
  // than acting on "the selected one", so a row can be deleted without
  // opening it.
  const onDeleteConversation = async (id) => {
    const ok = await confirm({
      title: 'Delete this conversation?',
      message: 'This permanently deletes the conversation and its messages for this customer. This cannot be undone.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    setDeletingId(id);
    if (id === selectedId) selfDeletedIdRef.current = id;
    try {
      await deleteConversation(id);
      if (selectedId === id) setSelectedId(null);
      await reloadList();
      addToast('Conversation deleted', { variant: 'success' });
    } catch (err) {
      if (selfDeletedIdRef.current === id) selfDeletedIdRef.current = null;
      addToast(parseApiError(err).formError || 'Could not delete the conversation', { variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const customerName = conversation?.user?.fName
    ? `${conversation.user.fName} ${conversation.user.lName || ''}`.trim()
    : null;
  const customerId = conversation?.user?._id || conversation?.user;

  // Just what the top (non-Advanced) part of the summary shows — the
  // Advanced copy button below builds on top of this rather than
  // duplicating these same fields.
  const buildBasicSummaryLines = () => [
    `Conversation: ${conversation?.title || 'Untitled conversation'}`,
    `Status: ${conversation?.mode === 'human' ? 'With a person' : 'Bot'}`,
    `Started: ${conversation?.createdAt ? new Date(conversation.createdAt).toLocaleString() : '—'}`,
    `Customer: ${customerName || '—'}`,
    `User ID: ${customerId || '—'}`,
  ];

  const copyToClipboard = async (lines, successMessage) => {
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      addToast(successMessage, { variant: 'success' });
    } catch {
      addToast('Could not copy the chat info', { variant: 'error' });
    }
  };

  const onCopyBasic = () => copyToClipboard(buildBasicSummaryLines(), 'Chat info copied to clipboard');

  // Everything the Advanced panel shows, as plain text — for pasting into a
  // support ticket/Slack thread without retyping every field.
  const onCopyOverview = () =>
    copyToClipboard(
      [
        ...buildBasicSummaryLines(),
        `Conversation ID: ${conversation?._id || selectedId}`,
        `Messages: ${messages.length}`,
        `Provider / model: ${conversation?.provider ? `${conversation.provider} / ${conversation.model}` : '—'}`,
        `Tokens: ${aiStats ? `${aiStats.inputTokens + aiStats.outputTokens} (${aiStats.inputTokens} in / ${aiStats.outputTokens} out)` : '—'}`,
        `Tool iterations: ${aiStats ? aiStats.toolCallRounds : '—'}`,
        `AI-answered turns: ${aiStats ? aiStats.turnCount : '—'}`,
      ],
      'Chat info copied to clipboard'
    );

  return (
    <div>
      <h1>Chat Handoff</h1>
      <p style={{ color: 'var(--ss-brown-hover)', marginTop: 'var(--ss-space-1)' }}>
        Select a conversation for an overview, then Interact to take it over and reply.
      </p>

      <div style={{ marginTop: 'var(--ss-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--ss-space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)', flexWrap: 'wrap' }}>
          <span className="ss-admin-card__label">Mode</span>
          <div className="ss-admin-filters" role="group" aria-label="Filter by mode">
            {MODE_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all'}
                type="button"
                className={`ss-chip ${mode === value ? 'is-active' : ''}`}
                onClick={() => setFilter(value)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {value === 'bot' && <Bot size={13} aria-hidden="true" />}
                  {value === 'human' && <User size={13} aria-hidden="true" />}
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)', flexWrap: 'wrap' }}>
          <span className="ss-admin-card__label">Status</span>
          <div className="ss-admin-filters" role="group" aria-label="Filter by status">
            {STATUS_FILTERS.map(({ value, label }) => (
              <button
                key={value || 'all'}
                type="button"
                className={`ss-chip ${status === value ? 'is-active' : ''}`}
                onClick={() => setStatusFilter(value)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {value === 'active' && <MessageCircle size={13} aria-hidden="true" />}
                  {value === 'archived' && <Lock size={13} aria-hidden="true" />}
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--ss-space-2)', flexWrap: 'wrap' }}>
          <span className="ss-admin-card__label">Handoff</span>
          <div className="ss-admin-filters" role="group" aria-label="Filter by handoff request">
            {HANDOFF_FILTERS.map(({ value, label }) => (
              <button
                key={value ? 'person' : 'all'}
                type="button"
                className={`ss-chip ${handoffOnly === value ? 'is-active' : ''}`}
                onClick={() => setHandoffFilter(value)}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {value && <Hand size={13} aria-hidden="true" />}
                  {label}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <Card className="ss-chat-console__list" style={{ padding: 0, marginTop: 'var(--ss-space-4)' }}>
        {listLoading ? (
          <div className="ss-state">
            <Spinner label="Loading conversations" size={28} />
          </div>
        ) : items.length === 0 ? (
          <div className="ss-state">
            <h2>No conversations</h2>
          </div>
        ) : (
          <ul className="ss-chat-console__conversations">
            {items.map((c) => (
              <li key={c._id} className={`ss-chat-console__conversation-row ${selectedId === c._id ? 'is-active' : ''}`}>
                <button type="button" className="ss-chat-console__conversation" onClick={() => setSelectedId(c._id)}>
                  <span className="ss-chat-console__conversation-main">
                    {c.handoffRequested && (
                      <span className="ss-chat-console__status-icon ss-chat-console__status-icon--handoff" title="Person requested">
                        <Hand size={13} aria-hidden="true" />
                      </span>
                    )}
                    <span
                      className={`ss-chat-console__status-icon ss-chat-console__status-icon--${
                        c.status === 'archived' ? 'closed' : 'open'
                      }`}
                      title={c.status === 'archived' ? 'Closed' : 'Open'}
                    >
                      {c.status === 'archived' ? (
                        <Lock size={13} aria-hidden="true" />
                      ) : (
                        <MessageCircle size={13} aria-hidden="true" />
                      )}
                    </span>
                    {c.mode === 'human' ? (
                      <span className="ss-chat-console__status-icon" title="Person">
                        <User size={13} aria-hidden="true" />
                      </span>
                    ) : (
                      <span className="ss-chat-console__status-icon" title="Bot">
                        <Bot size={13} aria-hidden="true" />
                      </span>
                    )}
                    <span className="ss-chat-console__conversation-title">{c.title || 'Untitled conversation'}</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="ss-icon-btn ss-icon-btn--danger ss-chat-console__conversation-delete"
                  disabled={deletingId === c._id || c.mode === 'human'}
                  title={c.mode === 'human' ? 'Hand back to the bot before deleting' : undefined}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteConversation(c._id);
                  }}
                  aria-label="Delete conversation"
                >
                  <Trash2 size={14} aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}

        {total > 0 && (
          <div className="ss-pager ss-pager--split" style={{ padding: 'var(--ss-space-3)' }}>
            <div className="ss-pager__nav">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
                Previous
              </Button>
              <span className="ss-pager__info">
                Page {page} of {pages} · {total} total
              </span>
              <Button variant="ghost" size="sm" disabled={page >= pages} onClick={() => setPage((n) => n + 1)}>
                Next
              </Button>
            </div>
            <div className="ss-pager__controls">
              <label className="ss-pager__page-size">
                Per page{' '}
                <select
                  className="ss-input ss-input--sm"
                  value={limit}
                  onChange={(e) => onChangeLimit(Number(e.target.value))}
                  aria-label="Conversations per page"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="ss-icon-btn"
                onClick={onRefresh}
                disabled={refreshing || listLoading}
                aria-label="Refresh"
                title="Refresh"
              >
                <RefreshCw size={16} aria-hidden="true" className={refreshing ? 'ss-spin' : ''} />
              </button>
            </div>
          </div>
        )}
      </Card>

      {selectedId && (
        <Card style={{ marginTop: 'var(--ss-space-4)' }}>
          {overviewLoading ? (
            <div className="ss-state">
              <Spinner label="Loading overview" size={28} />
            </div>
          ) : (
            <>
              <div className="ss-chat-console__overview-head">
                <div className="ss-chat-console__overview-head-top">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--ss-space-2)' }}>
                    {conversation?.handoffRequested && (
                      <span
                        className="ss-chat-console__status-icon ss-chat-console__status-icon--handoff"
                        title={
                          conversation.handoffRequestedAt
                            ? `Person requested — ${new Date(conversation.handoffRequestedAt).toLocaleString()}`
                            : 'Person requested'
                        }
                      >
                        <Hand size={16} aria-hidden="true" />
                      </span>
                    )}
                    <span
                      className={`ss-chat-console__status-icon ss-chat-console__status-icon--${
                        conversation?.status === 'archived' ? 'closed' : 'open'
                      }`}
                      title={conversation?.status === 'archived' ? 'Closed' : 'Open'}
                    >
                      {conversation?.status === 'archived' ? (
                        <Lock size={16} aria-hidden="true" />
                      ) : (
                        <MessageCircle size={16} aria-hidden="true" />
                      )}
                    </span>
                    <span
                      className="ss-chat-console__status-icon"
                      title={conversation?.mode === 'human' ? 'Person' : 'Bot'}
                    >
                      {conversation?.mode === 'human' ? (
                        <User size={16} aria-hidden="true" />
                      ) : (
                        <Bot size={16} aria-hidden="true" />
                      )}
                    </span>
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--ss-space-2)' }}>
                    <button
                      type="button"
                      className="ss-icon-btn"
                      onClick={onCopyBasic}
                      aria-label="Copy"
                      title="Copy"
                    >
                      <Copy size={16} aria-hidden="true" />
                    </button>
                    <Button size="sm" onClick={() => navigate(`/admin/chat/${selectedId}`)}>
                      Interact <ArrowRight size={14} aria-hidden="true" />
                    </Button>
                  </span>
                </div>
                <h2 className="ss-chat-console__overview-title">{conversation?.title || 'Untitled conversation'}</h2>
              </div>

              <div className="ss-chat-console__overview">
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Started</span>
                  <span>{conversation?.createdAt ? new Date(conversation.createdAt).toLocaleString() : '—'}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">Customer</span>
                  <span>{customerName || '—'}</span>
                </div>
                <div className="ss-admin-card__row">
                  <span className="ss-admin-card__label">User ID</span>
                  <span className="ss-chat-console__overview-id">{customerId}</span>
                </div>
              </div>

              {/* Everything specific to the AI portion of the chat, not the
                  handoff/customer-facing side — collapsed by default since
                  staff replying to a handoff don't need it open every time. */}
              <details className="ss-chat-console__advanced">
                <summary>Advanced</summary>
                <div className="ss-chat-console__advanced-head">
                  <button
                    type="button"
                    className="ss-icon-btn"
                    onClick={onCopyOverview}
                    aria-label="Copy"
                    title="Copy"
                  >
                    <Copy size={16} aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="ss-icon-btn"
                    onClick={onRefreshOverview}
                    disabled={overviewRefreshing || overviewLoading}
                    aria-label="Refresh"
                    title="Refresh"
                  >
                    <RefreshCw size={16} aria-hidden="true" className={overviewRefreshing ? 'ss-spin' : ''} />
                  </button>
                </div>
                <div className="ss-chat-console__overview">
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Conversation ID</span>
                    <span className="ss-chat-console__overview-id">{conversation?._id || selectedId}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Messages</span>
                    <span>{messages.length}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Provider / model</span>
                    <span>{conversation?.provider ? `${conversation.provider} / ${conversation.model}` : '—'}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Tokens</span>
                    <span>
                      {aiStats
                        ? `${aiStats.inputTokens + aiStats.outputTokens} (${aiStats.inputTokens} in / ${aiStats.outputTokens} out)`
                        : '—'}
                    </span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">Tool iterations</span>
                    <span>{aiStats ? aiStats.toolCallRounds : '—'}</span>
                  </div>
                  <div className="ss-admin-card__row">
                    <span className="ss-admin-card__label">AI-answered turns</span>
                    <span>{aiStats ? aiStats.turnCount : '—'}</span>
                  </div>
                </div>
              </details>
            </>
          )}
        </Card>
      )}

      {!selectedId && (
        <Card style={{ marginTop: 'var(--ss-space-4)' }}>
          <div className="ss-state">
            <MessageSquare size={32} aria-hidden="true" />
            <h2>Select a conversation for an overview</h2>
          </div>
        </Card>
      )}

      {dialog}
    </div>
  );
}
